/**
 * Générateur — Automatisme : reconnaitre un patron d'un cube.
 * Attendu ID : 1069 | Niveau : 5ème | Domaine : Représentation de l'espace
 * Rubrique : AUTOMATISME (programme 2025) — mobilisation en rituel court.
 * Labels (cartes exerciseur) :
 *   - Six carrés                    (réactivation : QCM à 5 questions)
 *   - Patron ou pas ?               (QCM planche ①-④, positif ou négatif ; V/F sur quatre figures ; QCM « pourquoi pas »)
 *   - Compléter le patron           (QCM : où placer le sixième carré)
 *   - Patron du cube : vrai ou faux ? (V/F de pur rappel)
 *
 * ── Plan de cartes validé (2026-09-04) ─────────────────────────────────────
 * Source : Automatisme n°31 du doc 5e + grille P1-P12 (analyse BO cycles 3-4).
 * P1 : INTRODUIT au CM1 (reconnaître et construire un patron du cube),
 * CONSOLIDÉ au CM2 (plus le patron du pavé), ABSENT de la 6e, automatisme en
 * 5e ; la mise en relation perspective ↔ patron (pavé, prisme, cylindre) est
 * un objectif de 5e, hors gen. P2 : sens installés — pliage et dépliage
 * concrets, compte des faces ; non installé — le raisonnement sans plier (la
 * bande de 4 fait le tour, un carré de chaque côté). Distracteurs : « 6 carrés
 * donc patron » (rectangle 2 × 3, bande de 6, 5 alignés), deux carrés du même
 * côté de la bande, mauvais nombre de carrés. P3 : 5, 6 ou 7 carrés, 3 paires
 * de faces. P5 : assemblages de carrés sur quadrillage. P6 : aucun
 * instrument ; le pliage est mental. P7 : pas de construction, pas de patron
 * du pavé / prisme / cylindre, pas de perspective ↔ patron. P8 : patron, face,
 * carré, plier, faces opposées, bande, se superposer. P9 : nature et nombre
 * des faces du cube restent implicites. P10 : constat par pliage mental ;
 * « lequel », « pourquoi » comme choix d'une raison. P11 : six carrés ne
 * suffisent pas ; deux carrés peuvent se superposer et une face rester
 * ouverte ; la bande de 4 fait le tour, un carré de chaque côté. P12 :
 * RÉMANENCE FAIBLE (rien en 6e) → carte de réactivation « Six carrés ».
 * INVARIANTS :
 *   - tout assemblage est TESTÉ par pliage (repère 3D propagé de carré en
 *     carré : un patron ⇔ six normales distinctes et cohérentes) ; les 11
 *     patrons du cube et les faux sont ainsi reconnus sans table ;
 *   - une planche n'a jamais deux assemblages de même forme (à rotation et
 *     retournement près) ; question négative → contenu.negated (conv. 1039) ;
 *   - data-cellules dans chaque figure pour les solveurs ; V/F : jamais de
 *     crochets, mixité 1-3 faux, fenêtre stricte.
 */
import {
    randomChoice, randInt, shuffle,
    makeQcm, makeVraiFaux,
} from '../../../utils';
import { imgElement } from '../statistiques/svgStats';

const ATTENDU_ID = 1069;
const opts = (label) => ({ niveau: '5eme', matiere: 'maths', attendu: ATTENDU_ID, label });

const L_RAPPEL = 'Six carrés';
const L_PATRON = 'Patron ou pas ?';
const L_BANDE = 'Compléter le patron';
const L_VF = 'Patron du cube : vrai ou faux ?';

const BLEU = '#1E3A8A';
const NUMS = ['①', '②', '③', '④'];

// ─── Polyominos : cellules, formes canoniques, pliage ──────────────────────

const cle = (c) => `${c[0]},${c[1]}`;
const normaliser = (cells) => {
    const mx = Math.min(...cells.map((c) => c[0])); const my = Math.min(...cells.map((c) => c[1]));
    return cells.map(([x, y]) => [x - mx, y - my]).sort((a, b) => a[0] - b[0] || a[1] - b[1]);
};
const cleCellules = (cells) => normaliser(cells).map(cle).join('|');
/** Forme canonique à rotation et retournement près (8 transformations, la plus petite chaîne). */
const canonique = (cells) => {
    const T = [([x, y]) => [x, y], ([x, y]) => [-y, x], ([x, y]) => [-x, -y], ([x, y]) => [y, -x],
        ([x, y]) => [-x, y], ([x, y]) => [y, x], ([x, y]) => [x, -y], ([x, y]) => [-y, -x]];
    return T.map((t) => cleCellules(cells.map(t))).sort()[0];
};

/**
 * Pliage : on propage un repère 3D (normale n, direction de +x r, direction de +y u) de carré en
 * carré ; passer au voisin +x tourne le repère autour de l'arête commune. Patron du cube ⇔ les six
 * normales sont distinctes ET tout chemin donne la même normale à un même carré (pas de 2 × 2).
 */
const estPatronCube = (cells) => {
    if (cells.length !== 6) return false;
    const set = new Set(cells.map(cle)); if (set.size !== 6) return false;
    const neg = (v) => v.map((a) => -a);
    const rep = new Map();
    const [c0] = cells;
    rep.set(cle(c0), { n: [0, 0, 1], r: [1, 0, 0], u: [0, 1, 0] });
    const file = [c0];
    while (file.length) {
        const c = file.shift(); const R = rep.get(cle(c));
        const voisins = [[[1, 0], () => ({ n: R.r, r: neg(R.n), u: R.u })], [[-1, 0], () => ({ n: neg(R.r), r: R.n, u: R.u })],
            [[0, 1], () => ({ n: R.u, r: R.r, u: neg(R.n) })], [[0, -1], () => ({ n: neg(R.u), r: R.r, u: R.n })]];
        for (const [[dx, dy], f] of voisins) {
            const v = [c[0] + dx, c[1] + dy]; if (!set.has(cle(v))) continue;
            const nouveau = f();
            const existant = rep.get(cle(v));
            if (existant) { if (existant.n.join() !== nouveau.n.join()) return false; continue; }
            rep.set(cle(v), nouveau); file.push(v);
        }
    }
    if (rep.size !== 6) return false;                       // non connexe
    return new Set([...rep.values()].map((R) => R.n.join())).size === 6;
};

/** Polyomino connexe aléatoire de n cellules (croissance par voisin). */
const polyominoAleatoire = (n) => {
    const cells = [[0, 0]]; const set = new Set(['0,0']);
    while (cells.length < n) {
        const c = randomChoice(cells); const [dx, dy] = randomChoice([[1, 0], [-1, 0], [0, 1], [0, -1]]);
        const v = [c[0] + dx, c[1] + dy]; if (set.has(cle(v))) continue;
        cells.push(v); set.add(cle(v));
    }
    return normaliser(cells);
};

/** Faux patrons de la fiche, à 6 carrés. */
const FAUX_FICHE = [
    [[0, 0], [1, 0], [2, 0], [0, 1], [1, 1], [2, 1]],           // rectangle 2 × 3
    [[0, 0], [1, 0], [2, 0], [3, 0], [4, 0], [5, 0]],           // bande de 6
    [[0, 0], [1, 0], [2, 0], [3, 0], [4, 0], [1, 1]],           // 5 alignés + 1
    [[0, 0], [1, 0], [2, 0], [3, 0], [0, 1], [3, 1]],           // bande de 4 + deux carrés du même côté
    [[0, 0], [1, 0], [2, 0], [3, 0], [1, 1], [2, 1]],           // bande de 4 + deux carrés côte à côte (bloc 2 × 2)
];
FAUX_FICHE.forEach((f) => { if (estPatronCube(f)) throw new Error('gen_1069 : un faux de la fiche se plie en cube'); });

/**
 * Les 35 hexominos libres, énumérés une fois par croissance, classés par pliage : 11 patrons et
 * 24 faux. Tirage UNIFORME dans chaque liste (revue 2026-09-05, A8 : la croissance aléatoire
 * servait le patron le plus compact 50 fois plus que le plus rare).
 */
const HEXOMINOS = (() => {
    let formes = new Map([['0,0', [[0, 0]]]]);
    for (let n = 1; n < 6; n++) {
        const suivantes = new Map();
        for (const cells of formes.values()) {
            const set = new Set(cells.map(cle));
            for (const c of cells) for (const [dx, dy] of [[1, 0], [-1, 0], [0, 1], [0, -1]]) {
                const v = [c[0] + dx, c[1] + dy]; if (set.has(cle(v))) continue;
                const nouvelle = normaliser([...cells, v]); const k = canonique(nouvelle);
                if (!suivantes.has(k)) suivantes.set(k, nouvelle);
            }
        }
        formes = suivantes;
    }
    const toutes = [...formes.values()];
    return { patrons: toutes.filter(estPatronCube), faux: toutes.filter((c) => !estPatronCube(c)) };
})();
if (HEXOMINOS.patrons.length !== 11 || HEXOMINOS.faux.length !== 24) throw new Error('gen_1069 : énumération des hexominos incohérente');

/** Un assemblage de 6 carrés : patron (true) ou non (false), tiré uniformément, forme canonique différente de `exclues`. */
const tirerHexomino = (patron, exclues = new Set()) => {
    for (let essai = 0; essai < 200; essai++) {
        const cells = patron ? randomChoice(HEXOMINOS.patrons) : (Math.random() < 0.4 ? randomChoice(FAUX_FICHE) : randomChoice(HEXOMINOS.faux));
        const k = canonique(cells); if (exclues.has(k)) continue;
        return { cells: normaliser(cells), k };
    }
    throw new Error('gen_1069 : hexomino introuvable');
};

/** Transformation aléatoire (rotation / retournement) pour ne pas toujours montrer la même orientation. */
const orienter = (cells) => {
    const T = [([x, y]) => [x, y], ([x, y]) => [-y, x], ([x, y]) => [-x, -y], ([x, y]) => [y, -x],
        ([x, y]) => [-x, y], ([x, y]) => [y, x], ([x, y]) => [x, -y], ([x, y]) => [-y, -x]];
    return normaliser(cells.map(randomChoice(T)));
};

// ─── Rendu ─────────────────────────────────────────────────────────────────

const CELL = 15;

/** Cellules pleines (et pointillées optionnelles avec lettre), centrées dans un panneau. */
const dessinerCellules = (cells, ox, oy, w, h, pointilles = []) => {
    const toutes = [...cells, ...pointilles.map((p) => p.c)];
    const mx = Math.min(...toutes.map((c) => c[0])); const Mx = Math.max(...toutes.map((c) => c[0]));
    const my = Math.min(...toutes.map((c) => c[1])); const My = Math.max(...toutes.map((c) => c[1]));
    const gw = (Mx - mx + 1) * CELL; const gh = (My - my + 1) * CELL;
    const x0 = ox + Math.round((w - gw) / 2); const y0 = oy + Math.round((h - gh) / 2);
    // y du quadrillage vers le HAUT : ligne y = My en haut du dessin
    const X = (c) => x0 + (c[0] - mx) * CELL; const Y = (c) => y0 + (My - c[1]) * CELL;
    let s = '';
    cells.forEach((c) => { s += `<rect x="${X(c)}" y="${Y(c)}" width="${CELL}" height="${CELL}" fill="#bfdbfe" stroke="#1e293b" stroke-width="1.3"/>`; });
    pointilles.forEach((p) => {
        s += `<rect x="${X(p.c) + 1}" y="${Y(p.c) + 1}" width="${CELL - 2}" height="${CELL - 2}" fill="#fff" stroke="#B45309" stroke-width="1.2" stroke-dasharray="3 2"/>`
            + `<text x="${X(p.c) + CELL / 2}" y="${Y(p.c) + CELL / 2 + 4}" font-size="10" font-weight="800" fill="#B45309" text-anchor="middle">${p.lettre}</text>`;
    });
    return s;
};

const svgPlanche = (figures, extra = '') => {
    const PW = 118; const PH = 100;
    let s = '';
    figures.forEach((cells, i) => {
        const ox = (i % 2) * PW; const oy = Math.floor(i / 2) * PH;
        s += `<g data-index="${i + 1}" data-cellules="${cleCellules(cells)}">`
            + `<rect x="${ox + 1}" y="${oy + 1}" width="${PW - 2}" height="${PH - 2}" fill="#fff" stroke="#e2e8f0"/>`
            + `<text x="${ox + 7}" y="${oy + 17}" font-size="14" font-weight="800" fill="${BLEU}">${NUMS[i]}</text>`
            + dessinerCellules(cells, ox + 14, oy + 18, PW - 22, PH - 24)
            + '</g>';
    });
    return `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 ${2 * PW} ${2 * PH}" width="${2 * PW}" height="${2 * PH}" font-family="Arial"${extra}>${s}</svg>`;
};

const svgFigure = (cells, pointilles = [], extra = '') => {
    const toutes = [...cells, ...pointilles.map((p) => p.c)];
    // Cadre d'au moins 150 × 110 : une bande verticale de 6 carrés, mise à la largeur de son conteneur
    // par l'application, prenait toute la hauteur de l'écran (retour prof 2026-09-05).
    const w = Math.max(150, (Math.max(...toutes.map((c) => c[0])) - Math.min(...toutes.map((c) => c[0])) + 1) * CELL + 24);
    const h = Math.max(110, (Math.max(...toutes.map((c) => c[1])) - Math.min(...toutes.map((c) => c[1])) + 1) * CELL + 24);
    return `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 ${w} ${h}" width="${w}" height="${h}" font-family="Arial" data-cellules="${cells.map(cle).join('|')}"${extra}>${dessinerCellules(cells, 0, 0, w, h, pointilles)}</svg>`;
};

// ─── Carte 1 : six carrés (réactivation, un seul QCM à 5 questions) ───────
// Pool structurellement borné (pur rappel de la fiche) : les trois comptes de la série flash
// en QCM et deux questions argumentées ; un seul format pour rester au-dessus du seuil de
// variété du harnais (5 empreintes).

// Longueurs des options homogénéisées (revue 2026-09-05, A2).
const QUESTIONS_RAPPEL = [
    { id: 'carres', q: 'Combien de carrés doit comporter un patron du cube ?', bonne: '6 carrés', leurres: ['4 carrés', '8 carrés', '12 carrés'] },
    { id: 'paires', q: 'Combien un cube a-t-il de paires de faces opposées ?', bonne: '3 paires', leurres: ['2 paires', '4 paires', '6 paires'] },
    { id: 'tour', q: 'Dans un patron du cube, combien de carrés alignés suffisent pour faire le tour du cube (ses faces latérales) ?', bonne: '4 carrés', leurres: ['3 carrés', '5 carrés', '6 carrés'] },
    {
        id: 'identiques',
        q: 'Les six carrés d\'un patron du cube doivent-ils être identiques ?',
        bonne: 'Oui : ce sont les six faces du cube, toutes de même taille.',
        leurres: ['Non : les faces de côté peuvent être plus petites que le dessus.', 'Non : seule la face du dessus doit être un carré.', 'Oui, mais il en faut huit, un par sommet du cube.'],
    },
    {
        id: 'suffisent',
        q: 'Un assemblage de six carrés identiques est-il toujours un patron du cube ?',
        bonne: 'Non : en pliant, chaque carré doit devenir une face différente.',
        leurres: ['Oui : six carrés, ce sont exactement les six faces du cube.', 'Oui, à condition que les carrés soient bien identiques.', 'Non : il faut huit carrés pour fermer complètement un cube.'],
    },
];

let derniereQuestionRappel1069 = null;

const genRappelQcm = () => {
    let i = randInt(0, QUESTIONS_RAPPEL.length - 1);
    if (i === derniereQuestionRappel1069) i = (i + 1) % QUESTIONS_RAPPEL.length;
    derniereQuestionRappel1069 = i;
    const Q = QUESTIONS_RAPPEL[i];
    const payload = makeQcm(shuffle([Q.bonne, ...Q.leurres]), Q.bonne, `<p>${Q.q}</p>`, opts(L_RAPPEL));
    payload.contenu = { ...payload.contenu, sousType: Q.id };
    payload._meta.params = { question: Q.id, signature: Q.id };
    return payload;
};

// ─── Carte 2a : patron ou pas ? (planche) ──────────────────────────────────

let derniereSignaturePlanche1069 = null;

const genPatronQcm = () => {
    let payload = construirePatronQcm();
    for (let t = 0; t < 8 && payload._meta.params.signature === derniereSignaturePlanche1069; t++) payload = construirePatronQcm();
    derniereSignaturePlanche1069 = payload._meta.params.signature;
    return payload;
};

const construirePatronQcm = () => {
    const negatif = Math.random() < 0.4;
    const exclues = new Set(); const figures = [];
    // positif : 1 patron + 3 faux ; négatif : 3 patrons + 1 faux
    const nbPatrons = negatif ? 3 : 1;
    for (let i = 0; i < 4; i++) { const f = tirerHexomino(i < nbPatrons, exclues); exclues.add(f.k); figures.push({ cells: orienter(f.cells), patron: i < nbPatrons }); }
    const melange = shuffle(figures);
    const bonneIndex = melange.findIndex((f) => f.patron !== negatif);
    const options = NUMS.map((n) => `Figure ${n}`);
    const question = negatif ? 'Une seule de ces figures N\'EST PAS un patron du cube. Laquelle ?' : 'Une seule de ces figures est un patron du cube. Laquelle ?';
    const svg = svgPlanche(melange.map((f) => f.cells));
    const payload = makeQcm(options, options[bonneIndex], `<p>Chaque figure est un assemblage de six carrés identiques.</p><p>${question}</p>`, {
        ...opts(L_PATRON), elements_additionnels: imgElement(svg, 'medium'),
    });
    payload.contenu = { ...payload.contenu, cadence: 'raisonnement', sousType: negatif ? 'negatif' : 'positif' };
    if (negatif) payload.contenu = { ...payload.contenu, negated: true };
    payload._meta.params = { negatif, bonneIndex, signature: `${negatif ? 'N' : 'P'}-${melange.map((f) => canonique(f.cells)).join('/')}` };
    return payload;
};

// ─── Carte 2b : patron ou pas ? (V/F sur quatre figures) ───────────────────

let dernieresLignesVf1069 = [];

const genPatronVf = () => {
    for (let essai = 0; essai < 160; essai++) {
        const nbFaux = randInt(1, 3);                        // lignes « est un patron » fausses = figures non patrons
        const exclues = new Set(); const figures = [];
        for (let i = 0; i < 4; i++) { const f = tirerHexomino(i >= nbFaux, exclues); exclues.add(f.k); figures.push({ cells: orienter(f.cells), patron: i >= nbFaux }); }
        const melange = shuffle(figures);
        const lignes = melange.map((f, i) => ({ l: `La figure ${NUMS[i]} est un patron du cube.`, v: f.patron }));
        const sig = melange.map((f) => canonique(f.cells)).join('/');
        if (essai > 120) dernieresLignesVf1069 = [];
        if (dernieresLignesVf1069.includes(sig)) continue;
        dernieresLignesVf1069 = [sig];
        const svg = svgPlanche(melange.map((f) => f.cells));
        const payload = makeVraiFaux(
            lignes.map((it) => it.l),
            lignes.map((it) => (it.v ? 0 : 1)),
            '<p>Chaque figure est un assemblage de six carrés identiques. Pour chaque affirmation, dis si elle est vraie ou fausse.</p>',
            { ...opts(L_PATRON), elements_additionnels: imgElement(svg, 'medium') },
        );
        payload.contenu = { ...payload.contenu, cadence: 'raisonnement' };
        payload._meta.params = { nbFaux, signature: sig };
        return payload;
    }
    throw new Error('gen_1069 : V/F patrons sans tirage après 160 essais');
};

// ─── Carte 2c : pourquoi pas ? (QCM) ───────────────────────────────────────

// Longueurs homogénéisées et leurre mort (« pas tous identiques », contredit par l'énoncé) remplacé
// par une idée fausse plausible — revue 2026-09-05, A2 / A3.
const RAISONS = {
    cinq: 'Il n\'y a que 5 carrés : il manque une face pour fermer le cube.',
    sept: 'Il y a 7 carrés : un carré de trop, qui se superposerait.',
    superpose: 'En pliant, deux carrés se superposent et une face reste ouverte.',
};
const RAISON_FAUSSE = 'Il y a bien 6 carrés, mais ils ne sont pas tous alignés.';

let derniereRaison1069 = null;

const genRaisonQcm = () => {
    let cas = randomChoice(['cinq', 'sept', 'superpose', 'superpose']);
    if (cas === derniereRaison1069) cas = randomChoice(['cinq', 'sept', 'superpose'].filter((c) => c !== cas));
    derniereRaison1069 = cas;
    let cells;
    if (cas === 'cinq') cells = orienter(polyominoAleatoire(5));
    else if (cas === 'sept') cells = orienter(polyominoAleatoire(7));
    else cells = orienter(tirerHexomino(false).cells);
    const options = shuffle([RAISONS.cinq, RAISONS.sept, RAISONS.superpose, RAISON_FAUSSE]);
    const svg = svgFigure(cells);
    const payload = makeQcm(options, RAISONS[cas], '<p>Cette figure est un assemblage de carrés identiques, mais ce n\'est pas un patron du cube.</p><p>Pourquoi ?</p>', {
        ...opts(L_PATRON), elements_additionnels: imgElement(svg, 'small'),
    });
    payload.contenu = { ...payload.contenu, cadence: 'raisonnement', sousType: cas };
    payload._meta.params = { cas, signature: `${cas}-${cleCellules(cells)}` };
    return payload;
};

// ─── Carte 3 : compléter le patron (QCM positions A-D) ─────────────────────

let derniereSignatureBande1069 = null;

const genBandeQcm = () => {
    let payload = construireBandeQcm();
    for (let t = 0; t < 8 && payload._meta.params.signature === derniereSignatureBande1069; t++) payload = construireBandeQcm();
    derniereSignatureBande1069 = payload._meta.params.signature;
    return payload;
};

const construireBandeQcm = () => {
    for (let essai = 0; essai < 100; essai++) {
        const i = randInt(0, 3);                              // colonne du cinquième carré, au-dessus de la bande
        const bande = [[0, 0], [1, 0], [2, 0], [3, 0], [i, 1]];
        const j = randInt(0, 3);                              // colonne du sixième carré, en dessous : la bonne
        const bonne = [j, -1];
        // positions fausses : au-dessus d'une autre colonne (même côté), au bout de la bande, sur le cinquième
        const memeCote = [randomChoice([0, 1, 2, 3].filter((c) => c !== i)), 1];
        const bout = randomChoice([[-1, 0], [4, 0]]);
        const dessus = [i, 2];
        // orientation aléatoire de la figure entière (revue 2026-09-05, A8 : bande toujours horizontale,
        // bonne réponse toujours « en dessous ») — la même transformation s'applique à la bande et aux candidats
        const T = randomChoice([([x, y]) => [x, y], ([x, y]) => [-y, x], ([x, y]) => [-x, -y], ([x, y]) => [y, -x],
            ([x, y]) => [-x, y], ([x, y]) => [y, x], ([x, y]) => [x, -y], ([x, y]) => [-y, -x]]);
        // bande et candidats recalés ensemble à l'origine : data-cellules et data-candidats partagent le même repère
        const brut = { c: shuffle([bonne, memeCote, bout, dessus]).map(T), b: bande.map(T) };
        const tout = [...brut.b, ...brut.c]; const mx = Math.min(...tout.map((c) => c[0])); const my = Math.min(...tout.map((c) => c[1]));
        const recale = ([x, y]) => [x - mx, y - my];
        const candidats = brut.c.map(recale); const bandeT = brut.b.map(recale);
        const verites = candidats.map((c) => estPatronCube([...bandeT, c]));
        if (verites.filter(Boolean).length !== 1) continue;
        const lettres = ['A', 'B', 'C', 'D'];
        const pointilles = candidats.map((c, k) => ({ c, lettre: lettres[k] }));
        const bonneIndex = verites.indexOf(true);
        const options = lettres.map((L) => `Position ${L}`);
        const svg = svgFigure(bandeT, pointilles, ` data-candidats="${candidats.map(cle).join('|')}"`);
        const payload = makeQcm(options, options[bonneIndex], '<p>La bande de quatre carrés fera le tour du cube, et un cinquième carré est déjà accolé. Les positions A, B, C et D sont dessinées en pointillés.</p><p>Où placer le sixième carré pour obtenir un patron du cube ?</p>', {
            ...opts(L_BANDE), elements_additionnels: imgElement(svg, 'small'),
        });
        payload.contenu = { ...payload.contenu, cadence: 'raisonnement' };
        payload._meta.params = { i, j, bonneIndex, signature: `${i}-${j}-${candidats.map(cle).join('|')}` };
        return payload;
    }
    throw new Error('gen_1069 : bande sans position unique');
};

// ─── Carte 4 : vrai ou faux ? ──────────────────────────────────────────────

const POOL_VF = [
    { l: 'Un patron du cube est formé de 6 carrés identiques.', v: true },
    { l: 'Un cube a 3 paires de faces opposées.', v: true },
    { l: 'Une bande de 4 carrés alignés forme le tour du cube.', v: true },
    { l: 'Il existe plusieurs patrons différents du cube.', v: true },
    { l: 'Sur un patron du cube, deux faces opposées ne sont jamais côte à côte.', v: true },
    { l: 'En pliant un patron du cube, chaque carré devient une face différente.', v: true },
    { l: 'Un rectangle de 2 carrés sur 3 est un patron du cube.', v: false },
    { l: 'Une bande de 6 carrés alignés est un patron du cube.', v: false },
    { l: 'Tout assemblage de 6 carrés identiques est un patron du cube.', v: false },
    { l: 'Un patron du cube peut avoir 5 carrés si on plie bien.', v: false },
    { l: 'Il n\'existe qu\'un seul patron du cube.', v: false },
    { l: 'Dans un patron du cube, 5 carrés peuvent être alignés.', v: false },
];
if (POOL_VF.filter((it) => it.v).length !== 6) throw new Error('gen_1069 : pool V/F déséquilibré');
POOL_VF.forEach((it) => { if (/[[\]]/.test(it.l)) throw new Error('gen_1069 : crochets dans une ligne V/F'); });

// Paires vrai / faux sur le même fait, jamais servies ensemble (revue 2026-09-05, A8 : 54 % des tableaux).
const FAMILLE_VF_1069 = (l) => {
    if (/formé de 6 carrés identiques|5 carrés si on plie/.test(l)) return 'six';
    if (/plusieurs patrons différents|qu'un seul patron/.test(l)) return 'plusieurs';
    if (/chaque carré devient une face différente|Tout assemblage de 6 carrés/.test(l)) return 'assemblage';
    if (/bande de 6|5 carrés peuvent être alignés/.test(l)) return 'alignes';
    return l;
};

let derniersServisVf1069 = [];

const genVf = () => {
    for (let essai = 0; essai < 120; essai++) {
        const dispo = POOL_VF.filter((it) => !derniersServisVf1069.includes(it.l));
        const tirage = shuffle(dispo).slice(0, 4);
        const nbFaux = tirage.filter((it) => !it.v).length;
        if (nbFaux < 1 || nbFaux > 3) continue;
        if (new Set(tirage.map((it) => FAMILLE_VF_1069(it.l))).size !== 4) continue;
        derniersServisVf1069 = tirage.map((it) => it.l);
        const payload = makeVraiFaux(
            tirage.map((it) => it.l),
            tirage.map((it) => (it.v ? 0 : 1)),
            '<p>Patron du cube. Pour chaque affirmation, dis si elle est vraie ou fausse.</p>',
            opts(L_VF),
        );
        payload.contenu = { ...payload.contenu, cadence: 'raisonnement' };
        return payload;
    }
    throw new Error('gen_1069 : V/F sans mixité après 60 essais');
};

// ─── Registre ──────────────────────────────────────────────────────────────

const GENERATORS = {
    rappel_qcm_unique: genRappelQcm,
    patron_qcm_unique: genPatronQcm,
    patron_tableau_vf: genPatronVf,
    patron_raison_qcm_unique: genRaisonQcm,
    bande_qcm_unique: genBandeQcm,
    vf_tableau_vf: genVf,
};

const FORMAT_LABELS = { saisie: 'Saisie', qcm_unique: 'QCM', raison_qcm_unique: 'Pourquoi pas ? (QCM)', tableau_vf: 'Vrai/Faux' };

const buildFormats = (prefix) =>
    Object.keys(GENERATORS).filter((k) => k.startsWith(`${prefix}_`))
        .map((k) => ({ key: k, label: FORMAT_LABELS[k.slice(prefix.length + 1)] || k }));

export const CATEGORIES = [
    { id: 'rappel', label: L_RAPPEL, formatsList: buildFormats('rappel') },
    { id: 'patron', label: L_PATRON, formatsList: buildFormats('patron') },
    { id: 'bande', label: L_BANDE, formatsList: buildFormats('bande') },
    { id: 'vf', label: L_VF, formatsList: buildFormats('vf') },
];

export const generate = (typeExercice) => {
    let key;
    if (typeExercice && GENERATORS[typeExercice]) key = typeExercice;
    else if (typeExercice) {
        const matching = Object.keys(GENERATORS).filter((k) => k.startsWith(typeExercice) || k.includes(typeExercice));
        key = matching.length ? randomChoice(matching) : randomChoice(Object.keys(GENERATORS));
    } else key = randomChoice(Object.keys(GENERATORS));
    const payload = GENERATORS[key]();
    payload._meta.generatorKey = key;
    return payload;
};

export const _internals = { estPatronCube, canonique };
